import type { PropsWithChildren, Ref } from 'react';

import { cva } from 'cva';
import { ButtonContext } from 'react-aria-components/Button';
import type { Key } from 'react-aria-components/Menu';
import { ToggleButtonContext } from 'react-aria-components/ToggleButton';
import type { ToggleButtonGroupProps as RACToggleButtonGroupProps } from 'react-aria-components/ToggleButtonGroup';
import { ToggleButtonGroup as RACToggleButtonGroup } from 'react-aria-components/ToggleButtonGroup';

import { useSelectedKeys } from '../../hooks/use_selected_keys';
import {
  type MultipleSelectionProps,
  type SingleSelectionProps,
  toItemKeys,
  toSelectedKeys,
} from '../../utils/selection_change';
import { ToggleButtonStyleContext } from '../toggle_button/toggle_button';

import styles from './toggle_button_group.module.css';

const toggleButtonGroupVariants = cva({
  base: styles.toggle_button_group,
  variants: {
    orientation: {
      horizontal: styles.orientation_horizontal,
      vertical: styles.orientation_vertical,
    },
    variant: {
      contained: styles.variant_contained,
      ghost: null,
    },
    size: {
      s: styles.size_s,
      m: styles.size_m,
      l: styles.size_l,
    },
    fullWidth: {
      true: styles.full_width,
    },
  },
  defaultVariants: {
    orientation: 'horizontal',
    variant: 'contained',
    size: 'm',
  },
});

type ToggleButtonGroupSelectionProps<ItemKey extends Key> =
  | ({ selectionMode?: 'single' } & SingleSelectionProps<ItemKey>)
  | ({ selectionMode: 'multiple' } & MultipleSelectionProps<ItemKey>);

export type ToggleButtonGroupProps<ItemKey extends Key = Key> = Omit<
  RACToggleButtonGroupProps,
  | 'selectionMode'
  | 'disallowEmptySelection'
  | 'selectedKeys'
  | 'defaultSelectedKeys'
  | 'onSelectionChange'
> &
  PropsWithChildren<{
    ref?: Ref<HTMLDivElement>;
    variant?: 'contained' | 'ghost';
    orientation?: 'horizontal' | 'vertical';
    size?: 's' | 'm' | 'l';
    iconPosition?: 'start' | 'top';
    prominent?: boolean;
    fullWidth?: boolean;
  }> &
  ToggleButtonGroupSelectionProps<ItemKey>;

/**
 * A toggle button group allows a user to toggle multiple options, with single or multiple selection.
 *
 * See https://react-spectrum.adobe.com/react-aria/ToggleButtonGroup.html for more details.
 *
 * ```tsx
 * <ToggleButtonGroup orientation="horizontal">
 *    <ToggleButton id="a">Option A</ToggleButton>
 *    <ToggleButton id="b">Option B</ToggleButton>
 *    <ToggleButton id="c">Option C</ToggleButton>
 * </ToggleButtonGroup>
 * ```
 */
export function ToggleButtonGroup<ItemKey extends Key = Key>(
  props: ToggleButtonGroupProps<ItemKey>,
) {
  const {
    orientation,
    variant,
    size,
    iconPosition,
    className,
    children,
    prominent,
    isDisabled,
    fullWidth,
    selection,
    defaultSelection,
    ref,
    ...rest
  } = props;
  const selectedKeys = useSelectedKeys(selection);
  const handleSelectionChange = (newSelection: Set<Key>) => {
    const keys = toItemKeys<ItemKey>(newSelection);
    const [key] = keys;
    if (props.selectionMode === 'multiple') {
      props.onSelectionChange?.(keys);
    } else if (props.disallowEmptySelection) {
      if (key !== undefined) {
        props.onSelectionChange?.(key);
      }
    } else {
      props.onSelectionChange?.(key ?? null);
    }
  };
  const classes = toggleButtonGroupVariants({
    orientation,
    variant,
    size,
    fullWidth,
    className,
  });
  return (
    <RACToggleButtonGroup
      orientation={orientation}
      isDisabled={isDisabled}
      selectedKeys={selectedKeys}
      defaultSelectedKeys={toSelectedKeys(defaultSelection)}
      {...rest}
      onSelectionChange={handleSelectionChange}
      className={classes}
      ref={ref}
    >
      <ButtonContext.Provider value={{ isDisabled }}>
        {/* TODO: Should not be necessary, see https://github.com/adobe/react-spectrum/issues/7798. */}
        <ToggleButtonContext.Provider value={{ isDisabled }}>
          <ToggleButtonStyleContext.Provider
            value={{
              variant: 'ghost',
              size,
              prominent,
              iconPosition,
              fullWidth,
            }}
          >
            {children}
          </ToggleButtonStyleContext.Provider>
        </ToggleButtonContext.Provider>
      </ButtonContext.Provider>
    </RACToggleButtonGroup>
  );
}
